import { cloneDeep, debounce, pick } from 'lodash';
import { BaseListState, DefaultAclPrefix, AutoUpdateIntervalInMs, getPageFromNextPage, IIntervalData, PartialWithId } from '@/definitions/common/base';
import { DataService } from '@/definitions/services/data.services';
import { Filter } from '@/definitions/view-models/filter';
import { ItemViewModel } from '@/definitions/view-models/item.view.model';
import { autoUpdateHelper } from '@/api/common/auto-update-helper';
import { AclModuleAbstract, ModelAclResult } from '@/store/acl/types';

const DefaultUpdateInterval = 5 * 60 * 1000;
const UpdateStatisticsIntervalMs = Math.max(+(localStorage?.debugUpdateInterval || DefaultUpdateInterval), 1000);
let IntervalItems = 0;
let ModuleItems = 0;

setInterval(
  () =>
    localStorage?.debugUpdateInterval &&
    console.log(`Active intervals: ${IntervalItems}, interval ${UpdateStatisticsIntervalMs}ms, total modules ${ModuleItems}`),
  3000
);

export class ListViewModel<T, F> implements BaseListState<T, F> {
  name = 'base';
  apiName = 'api_name';
  aclPrefix = DefaultAclPrefix;
  aclModelName: string | undefined;
  excludedChangeKeys: string[] = [];
  version = 2;
  syncFiltersToRoute = false;

  playing = false;
  loading = false;
  saving = false;
  deleting = false;
  appending = false;
  loaded = false;
  loadError: any | null = null;
  prevPages: string[] = [];
  items: T[] = [];
  emptyItem?: T;
  protected _aclModel?: AclModuleAbstract;

  mockItemOnLoad?: (item: any) => any;

  firstLoadingPromise!: Promise<void>;
  protected resolveFirstLoadingPromise!: () => void;
  protected rejectFirstLoadingPromise!: () => void;

  count = 0;
  updateStatisticsInterval = 0;

  protected _dataService: DataService<T, F> | undefined;

  autoUpdate: IIntervalData = {
    enabled: false,
    intervalInMs: AutoUpdateIntervalInMs,
    intervalIndex: 0
  };

  autoUpdateWithHelper = true;
  debouncedGet: any = null;
  debouncedGetStatistic: any = null;
  autoUpdateDebounceGet: any = null;

  page = '';
  next_page: string | null = null;
  prev_page: string[] = [];
  limits: number[] = [10, 20, 50, 100, 200, 500];

  filter = new Filter<F>();

  constructor() {
    ModuleItems++;
    this.autoUpdateDebounceGet = debounce<any>(this.get, 600);
    this.debouncedGet = debounce<any>(this.get, 600);
    this.debouncedGetStatistic = debounce<any>(this.getStatistics, 8000);
    this.getStatistics = debounce<any>(this.getStatistics, 600);
    this.firstLoadingPromise = new Promise<void>((resolve, reject) => {
      this.resolveFirstLoadingPromise = resolve;
      this.rejectFirstLoadingPromise = reject;
    }).catch((e) => console.warn('[lvm] first promise has occurred an error: ', e));
  }

  get filterChanges() {
    return this.filter.changes;
  }

  get dataService(): DataService<T, F> {
    if (!this._dataService) {
      throw new Error('Data Service should be initialized');
    } else {
      return this._dataService;
    }
  }

  set dataService(v: DataService<T, F>) {
    this._dataService = v;
  }

  set aclModule(v: AclModuleAbstract) {
    this._aclModel = v;
  }

  get acl(): ModelAclResult {
    if (!this._aclModel) {
      throw new Error(`ViewModel: ${this.name} has no acl model to compute acl`);
    }
    return this._aclModel.getModelAcl(this);
  }

  getItemViewModelByItem(item: T): ItemViewModel<T> {
    const wrappedItem: ItemViewModel<T> = new ItemViewModel();
    wrappedItem.name = this.name;
    wrappedItem.setItemsState(item);
    wrappedItem.dataService = this.dataService;
    wrappedItem.excludedChangeKeys = this.excludedChangeKeys;
    wrappedItem.aclModelName = this.aclModelName;
    return wrappedItem;
  }

  get hasAcl(): boolean {
    return !!this.aclModelName;
  }

  get aclViewPermissionName(): string {
    return `${this.aclPrefix}.view_${this.aclModelName}`;
  }

  get aclAddPermissionName(): string {
    return `${this.aclPrefix}.add_${this.aclModelName}`;
  }

  get aclUpdatePermissionName(): string {
    return `${this.aclPrefix}.change_${this.aclModelName}`;
  }

  get aclDeletePermissionName(): string {
    return `${this.aclPrefix}.delete_${this.aclModelName}`;
  }

  public resetFilters(): void {
    this.filter.reset();
  }

  init(emptyItem: T, emptyFilter: F, filterSchema?: any) {
    this.emptyItem = cloneDeep(emptyItem);
    this.filter = new Filter<F>(emptyFilter, filterSchema);
  }

  async get(options = { resetState: false }): Promise<boolean> {
    if (this.loading || this.appending) return false;
    if (options.resetState) this.items = [];
    this.setLoading(true);

    try {
      const responseData = await this.dataService.getList(this.filter.final);
      const items = (responseData.results || []).map((v) => (this.mockItemOnLoad ? this.mockItemOnLoad(v) : v));
      if (Number.isFinite(responseData.total)) this.count = Number(responseData.total);
      this.items = items;
      this.next_page = responseData.next_page;
      this.resolveFirstLoadingPromise();
    } catch (e) {
      this.loadError = e;
      this.items = [];
      this.next_page = null;
      this.rejectFirstLoadingPromise();
    }

    this.setLoading(false);
    this.setAutoUpdate(this.autoUpdate.enabled);
    return this.loadError ? Promise.reject(this.loadError) : true;
  }

  setLoading(value: boolean) {
    this.loading = value;

    if (this.loading) {
      this.loadError = null;
    } else {
      this.loaded = true;
    }
  }

  setAutoUpdate(value: boolean, intervalInMs?: number) {
    clearInterval(this.autoUpdate.intervalIndex);
    this.autoUpdate.enabled = value;
    this.autoUpdate.intervalInMs = intervalInMs || this.autoUpdate.intervalInMs;
    if (value) {
      this.autoUpdate.intervalIndex = window.setInterval(() => this.get({ resetState: false }), this.autoUpdate.intervalInMs);
    }
  }

  async append(): Promise<boolean> {
    const nextPage = this.next_page && getPageFromNextPage(this.next_page);
    if (!nextPage) return false;
    if (this.loading || this.appending) return false;

    this.appending = true;
    this.loadError = null;

    try {
      const responseData = await this.dataService.getList({ ...this.filter.final, page: nextPage });
      const itemsIdMap: Record<string, boolean> = this.items.reduce<Record<string, boolean>>((m, v: any) => ((m[String(v.id)] = true), m), {});
      const items = (responseData.results || []).map((v) => (this.mockItemOnLoad ? this.mockItemOnLoad(v) : v));
      const filteredAppendedItems = items?.filter((v: any) => !itemsIdMap[v.id]) ?? [];
      this.items = [...this.items, ...filteredAppendedItems];
      this.next_page = responseData.next_page;
    } catch (e) {
      this.loadError = e;
    }

    this.appending = false;
    return this.loadError ? Promise.reject(this.loadError) : true;
  }

  async create(data: Partial<T>): Promise<T> {
    const result = await this.dataService.create(data);
    autoUpdateHelper.createHandler(this.name, result);
    return result;
  }

  async update(data: PartialWithId<T>, fieldsToSync?: string[]): Promise<T> {
    const id: any = data.id;
    const result = await this.dataService.update(id, data);
    // console.log('Update result LVM ', result);
    const syncSource = fieldsToSync?.length ? pick(result, fieldsToSync.concat(['id'])) : result;
    autoUpdateHelper.updateHandler(this.name, syncSource);
    return result;
  }

  async delete(id: string | number): Promise<boolean> {
    const result = await this.dataService.delete(id);
    autoUpdateHelper.deleteHandler(this.name, id);
    return true;
  }

  reset(): void {
    this.items = [];
    this.next_page = null;
    this.loaded = false;
  }

  async getStatistics() {
    try {
      const responseData = await this.dataService.getStatistics(this.filter.final);
      this.count = responseData.count;
    } catch (e) {
      this.loadError = e;
    }
  }

  startStatisticsSync() {
    this.stopStatisticsSync();
    IntervalItems++;
    this.updateStatisticsInterval = window.setInterval(() => this.getStatistics(), UpdateStatisticsIntervalMs);
  }

  stopStatisticsSync() {
    if (this.updateStatisticsInterval) IntervalItems--;
    clearInterval(this.updateStatisticsInterval);
    this.updateStatisticsInterval = 0;
  }

  async saveItem(item: any): Promise<T | null | undefined> {
    const model = this.getItemViewModelByItem(item);
    return model.save();
  }
}
